import type { LaborPayStatus } from "./laborPayList.js";
import {
  laborPayCutoffAt,
  type LaborPayCutoffConfig,
} from "./laborPayWeek.js";

export type LaborPayWeekBillRow = {
  admitted_at?: string | null;
  payout_usd: number;
  status: LaborPayStatus;
  itemized?: boolean;
  unmatched_labor_count?: number;
  unmatched_model_count?: number;
  rate_item_count?: number;
  in_pay_trigger_column?: boolean;
};

export type LaborPayWeekBill = {
  start: Date;
  end: Date;
  buildings: number;
  totalUsd: number;
  exceptions: number;
  statusCounts: Array<{ status: LaborPayStatus; count: number }>;
  nextAction: string;
  earlierOpen: number;
};

const STATUS_ORDER: LaborPayStatus[] = [
  "open",
  "ops_ok",
  "plant_ok",
  "approved",
  "payment_requested",
  "batched",
  "void",
];

const STILL_OPEN = new Set<LaborPayStatus>(["open", "ops_ok", "plant_ok"]);

/** The open pay week: after the previous cutoff, through the upcoming cutoff. */
export function laborPayWeekWindow(
  now: Date,
  config: LaborPayCutoffConfig,
): { start: Date; end: Date } {
  const end = laborPayCutoffAt(now, config);
  const start = laborPayCutoffAt(new Date(end.getTime() - 8 * 24 * 60 * 60 * 1000), config);
  return { start, end };
}

export function laborPayRowInWeek(
  admittedAt: string | null | undefined,
  start: Date,
  end: Date,
): boolean {
  if (!admittedAt) return false;
  const at = Date.parse(admittedAt);
  if (Number.isNaN(at)) return false;
  return at > start.getTime() && at <= end.getTime();
}

function isException(row: LaborPayWeekBillRow): boolean {
  if (row.status === "void" || row.status === "batched") return false;
  if ((row.unmatched_labor_count ?? 0) > 0) return true;
  if ((row.unmatched_model_count ?? 0) > 0) return true;
  if (row.itemized && (row.rate_item_count ?? 0) === 0) return true;
  if (row.in_pay_trigger_column === false) return true;
  return false;
}

function money(n: number): number {
  return Math.round(n * 100) / 100;
}

function nextAction(rows: LaborPayWeekBillRow[], exceptions: number): string {
  const live = rows.filter((row) => row.status !== "void");
  if (live.length === 0) return "No buildings admitted this week.";
  if (exceptions > 0) {
    return `Resolve ${exceptions} exception${exceptions === 1 ? "" : "s"} before sending the bill.`;
  }
  if (live.some((row) => STILL_OPEN.has(row.status))) return "Stamp Labor OK on the buildings still open.";
  if (live.some((row) => row.status === "approved")) return "Request payment on the approved buildings.";
  if (live.some((row) => row.status === "payment_requested")) {
    return "Close the week's payout when the bill is ready to send.";
  }
  return "This week's bill is closed.";
}

export function summarizeLaborPayWeek(
  rows: readonly LaborPayWeekBillRow[],
  config: LaborPayCutoffConfig,
  now = new Date(),
): LaborPayWeekBill {
  const { start, end } = laborPayWeekWindow(now, config);
  const week = rows.filter((row) => laborPayRowInWeek(row.admitted_at, start, end));
  const billable = week.filter((row) => row.status !== "void" && row.status !== "batched");
  const counted = week.filter((row) => row.status !== "void");
  const counts = new Map<LaborPayStatus, number>();
  for (const row of week) counts.set(row.status, (counts.get(row.status) ?? 0) + 1);
  return {
    start,
    end,
    buildings: counted.length,
    totalUsd: money(billable.reduce((sum, row) => sum + (Number(row.payout_usd) || 0), 0)),
    exceptions: billable.filter(isException).length,
    statusCounts: STATUS_ORDER.filter((status) => counts.has(status)).map((status) => ({
      status,
      count: counts.get(status) ?? 0,
    })),
    nextAction: nextAction(week, billable.filter(isException).length),
    earlierOpen: rows.filter(
      (row) =>
        !laborPayRowInWeek(row.admitted_at, start, end) &&
        row.status !== "void" &&
        row.status !== "batched",
    ).length,
  };
}
